"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HIGHLIGHT_TONES,
  HighlightedText,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Checkbox,
  CopyButton,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  LABEL,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  APA_BLOCK_WORDS,
  MLA_BLOCK_LINES,
  type QuotationReport,
  quotationReport,
  type ReportedQuotation,
} from "@/lib/tool-kit/text-analysis/quotations";
import { collapseWhitespace } from "@/lib/tool-kit/text-analysis/segment";

/** English strings. A translated content file overrides these through `ui`. */
export const QUOTE_PERCENTAGE_CHECKER_STRINGS = {
  textLabel: "Your essay or paper",
  textPlaceholder:
    "Paste your draft. Quotations in double quotation marks — curly or straight — are found automatically.",
  trySample: "Try a sample",
  clear: "Clear",
  ruleLabel: "Block quotation rule",
  ruleApa: "APA 7 — 40 words or more",
  ruleMla: "MLA 9 — more than four lines",
  optionsLabel: "Options",
  singleQuotes: "Also count ‘single’ quotation marks (British style)",
  blockMarker: "Treat paragraphs that start with > as block quotations",
  ignoreReferences:
    "Ignore the reference list (after a References, Works Cited, or Bibliography heading)",
  ignoreShort:
    "Leave out quoted phrases of three words or fewer (titles, scare quotes)",
  emptyPrompt: "Paste your text to see how much of it is quoted.",
  results: "Results",
  percentQuoted: "Of your words are quoted",
  quotedWords: "Quoted words",
  totalWords: "Total words",
  quotations: "Quotations",
  summary:
    "one:Quotations make up {quoted} of your {total} word ({percent}).|other:Quotations make up {quoted} of your {total} words ({percent}).",
  noQuotations:
    "No quotations found. Check that each one is in double quotation marks — or switch on single quotation marks for British-style text.",
  referencesIgnored:
    "The reference list was left out, from the “{heading}” heading onward.",
  unmatched:
    "one: {count} quotation mark has no partner in its paragraph, so the text after it was not counted — look for a missing opening or closing mark.|other: {count} quotation marks have no partner in their paragraph, so the text after them was not counted — look for missing opening or closing marks.",
  guidance:
    "Neither APA nor MLA sets a maximum. If your instructor gives a guideline, that is the limit that counts.",
  copySummary: "Copy summary",
  copied: "Copied!",
  copyText:
    "one:{percent} quoted: {quoted} of {total} words, in {count} quotation.|other:{percent} quoted: {quoted} of {total} words, in {count} quotations.",
  highlightHeading: "Your text, quotations highlighted",
  highlightLabel: "Your text with quotations highlighted",
  legendQuotation: "Quotation",
  legendBlock: "Block quotation",
  legendTooLong: "Should be a block quotation",
  legendShort: "Not counted",
  listHeading: "Every quotation",
  colNumber: "#",
  colQuotation: "Quotation",
  colWords: "Words",
  colNote: "Note",
  noteApaBlock:
    "40 words or more — APA sets this as an indented block quotation.",
  noteMlaBlock:
    "one:About {lines} line — MLA sets anything over four lines as a block quotation.|other:About {lines} lines — MLA sets anything over four lines as a block quotation.",
  noteBlockShortApa:
    "Under 40 words — APA would run this into your paragraph in quotation marks.",
  noteBlockShortMla:
    "one:About {lines} line — MLA runs quotations of four lines or fewer into the text.|other:About {lines} lines — MLA runs quotations of four lines or fewer into the text.",
  noteBlock: "Block quotation.",
  noteShort: "Three words or fewer — left out of the total.",
  sample:
    "Sleep and Exam Performance\n\nMost students know that sleep matters, but few treat it as study time. Okafor (2021) describes overnight memory consolidation as “an active process, not a pause” (p. 14). In a survey of 412 undergraduates, Lindqvist and Mehta (2019) found that those who slept less than six hours before an exam said they “could read the questions but not reach the answers” (p. 7).\n\nThe same authors warn against reading too much into self-reports: “Students who sleep badly also tend to study late, start their assignments later, and report more stress, so a single survey cannot separate the effect of sleep itself from the effect of the late nights, the rushed revision, and the worry that travel with it” (Lindqvist & Mehta, 2019, p. 9). Even so, the pattern is consistent enough to act on.\n\nReferences\nLindqvist, E., & Mehta, R. (2019). Sleep before exams. Journal of Student Wellbeing, 12(2), 1–12.\nOkafor, C. (2021). The resting brain. Northfield University Press.",
} as const;

type Rule = "apa" | "mla";

const TONES = {
  quote: "amber",
  block: "sky",
  long: "rose",
  short: "violet",
} as const;

const EXCERPT_WORDS = 14;
const SPACE_RE = /\s+/;

function excerpt(text: string): string {
  const words = collapseWhitespace(text).split(SPACE_RE);
  return words.length > EXCERPT_WORDS
    ? `${words.slice(0, EXCERPT_WORDS).join(" ")}…`
    : words.join(" ");
}

function categoryOf(
  quotation: ReportedQuotation,
  rule: Rule
): keyof typeof TONES {
  if (!quotation.counted) {
    return "short";
  }
  if (quotation.block) {
    return "block";
  }
  const tooLong = rule === "apa" ? quotation.apaBlock : quotation.mlaBlock;
  return tooLong ? "long" : "quote";
}

type Strings = typeof QUOTE_PERCENTAGE_CHECKER_STRINGS;

function noteFor(
  quotation: ReportedQuotation,
  rule: Rule,
  strings: Strings,
  locale: string,
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string
): string {
  if (!quotation.counted) {
    return strings.noteShort;
  }
  const lines = Math.round(quotation.lines * 10) / 10;
  const linesText = (template: string) =>
    tPlural(template, locale, lines, {
      lines: fmt(lines, { maximumFractionDigits: 1 }),
    });
  if (quotation.block) {
    if (rule === "apa" && quotation.words < APA_BLOCK_WORDS) {
      return strings.noteBlockShortApa;
    }
    if (rule === "mla" && quotation.lines <= MLA_BLOCK_LINES) {
      return linesText(strings.noteBlockShortMla);
    }
    return strings.noteBlock;
  }
  if (rule === "apa") {
    return quotation.apaBlock ? strings.noteApaBlock : "";
  }
  return quotation.mlaBlock ? linesText(strings.noteMlaBlock) : "";
}

function Legend({ strings }: { strings: Strings }) {
  const items = [
    [TONES.quote, strings.legendQuotation],
    [TONES.block, strings.legendBlock],
    [TONES.long, strings.legendTooLong],
    [TONES.short, strings.legendShort],
  ] as const;
  return (
    <ul className="flex flex-wrap gap-3 text-muted-foreground text-xs">
      {items.map(([tone, label]) => (
        <li key={label}>
          <span className={HIGHLIGHT_TONES[tone]}>{label}</span>
        </li>
      ))}
    </ul>
  );
}

function QuotationTable({
  text,
  report,
  rule,
  strings,
}: {
  text: string;
  report: QuotationReport;
  rule: Rule;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.listHeading}</h3>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className={TABLE}>
          <thead>
            <tr>
              <th className={TH} scope="col">
                {strings.colNumber}
              </th>
              <th className={TH} scope="col">
                {strings.colQuotation}
              </th>
              <th className={`${TH} whitespace-nowrap`} scope="col">
                {strings.colWords}
              </th>
              <th className={TH} scope="col">
                {strings.colNote}
              </th>
            </tr>
          </thead>
          <tbody>
            {report.quotations.map((quotation, index) => (
              <tr key={quotation.start}>
                <td className={TD}>{fmt(index + 1)}</td>
                <td className={TD}>
                  {excerpt(
                    text.slice(quotation.innerStart, quotation.innerEnd)
                  )}
                </td>
                <td className={TD}>{fmt(quotation.words)}</td>
                <td className={`${TD} text-muted-foreground`}>
                  {noteFor(quotation, rule, strings, locale, fmt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Results({
  text,
  report,
  rule,
  strings,
}: {
  text: string;
  report: QuotationReport;
  rule: Rule;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const percent = fmt(report.percent / 100, {
    style: "percent",
    maximumFractionDigits: 1,
  });
  const values = {
    quoted: fmt(report.quotedWords),
    total: fmt(report.totalWords),
    percent,
    count: fmt(report.quotations.length),
  };
  const matches: TextMatch[] = report.quotations.map((quotation) => ({
    start: quotation.start,
    end: quotation.end,
    category: categoryOf(quotation, rule),
  }));
  const heading = report.referencesHeading;
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.percentQuoted} value={percent} />
        <StatCard label={strings.quotedWords} value={values.quoted} />
        <StatCard label={strings.totalWords} value={values.total} />
        <StatCard label={strings.quotations} value={values.count} />
      </StatGrid>
      <p className="text-foreground text-sm">
        {tPlural(strings.summary, locale, report.totalWords, values)}
      </p>
      {report.quotations.length === 0 ? (
        <Notice>{strings.noQuotations}</Notice>
      ) : null}
      {report.unmatched.length > 0 ? (
        <Notice>
          {tPlural(strings.unmatched, locale, report.unmatched.length, {
            count: fmt(report.unmatched.length),
          })}
        </Notice>
      ) : null}
      {heading === null ? null : (
        <p className={HINT}>{t(strings.referencesIgnored, { heading })}</p>
      )}
      <p className={HINT}>{strings.guidance}</p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copySummary}
        text={tPlural(
          strings.copyText,
          locale,
          report.quotations.length,
          values
        )}
      />
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.highlightHeading}</h3>
        <Legend strings={strings} />
        <HighlightedText
          label={strings.highlightLabel}
          matches={matches}
          text={text}
          tones={TONES}
        />
      </div>
      {report.quotations.length > 0 ? (
        <QuotationTable
          report={report}
          rule={rule}
          strings={strings}
          text={text}
        />
      ) : null}
    </section>
  );
}

export default function QuotePercentageCheckerWidget() {
  const strings = useToolUi(QUOTE_PERCENTAGE_CHECKER_STRINGS);
  const [text, setText] = useState("");
  const [rule, setRule] = useState<Rule>("apa");
  const [singleQuotes, setSingleQuotes] = useState(false);
  const [blockMarker, setBlockMarker] = useState(true);
  const [ignoreReferences, setIgnoreReferences] = useState(true);
  const [ignoreShort, setIgnoreShort] = useState(false);
  const deferred = useDeferredValue(text);
  const report = useMemo(
    () =>
      deferred.trim()
        ? quotationReport(deferred, {
            singleQuotes,
            blockMarker,
            ignoreReferences,
            ignoreShort,
          })
        : null,
    [deferred, singleQuotes, blockMarker, ignoreReferences, ignoreShort]
  );

  return (
    <>
      <TextAreaField
        id="qpc-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.textPlaceholder}
        prose
        rows={12}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.sample)}
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <PillGroup
        label={strings.ruleLabel}
        name="qpc-rule"
        onChange={setRule}
        options={[
          { value: "apa", label: strings.ruleApa },
          { value: "mla", label: strings.ruleMla },
        ]}
        value={rule}
      />
      <div className="space-y-2">
        <p className={LABEL}>{strings.optionsLabel}</p>
        <Checkbox
          checked={ignoreReferences}
          label={strings.ignoreReferences}
          onChange={setIgnoreReferences}
        />
        <Checkbox
          checked={blockMarker}
          label={strings.blockMarker}
          onChange={setBlockMarker}
        />
        <Checkbox
          checked={singleQuotes}
          label={strings.singleQuotes}
          onChange={setSingleQuotes}
        />
        <Checkbox
          checked={ignoreShort}
          label={strings.ignoreShort}
          onChange={setIgnoreShort}
        />
      </div>
      {report ? (
        <Results
          report={report}
          rule={rule}
          strings={strings}
          text={deferred}
        />
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
    </>
  );
}
